/**
 * rtDoseCore.mjs — ядро работы с RTDOSE (без браузера, проверяемо тестами).
 *
 *  - buildDoseGrid: пиксели RTDOSE × DoseGridScaling → доза в Гр (Float32);
 *  - doseSlice: срез дозовой матрицы (кадр f) как 2D-поле;
 *  - isodoseSegments: изодозные отрезки уровня L (marching squares с интерполяцией);
 *  - stitchSegments: сшивка отрезков в полилинии (замкнутые контуры);
 *  - doseGridPixelToWorld: перевод (col,row,frame) в мировые координаты (LPS);
 *  - doseStats / percentLevels: максимум и уровни в % от опорной дозы.
 */

// ── матрица дозы ────────────────────────────────────────────────────────────
export function buildDoseGrid(pixelData, doseGridScaling = 1) {
  const s = Number(doseGridScaling) || 1;
  const out = new Float32Array(pixelData.length);
  for (let i = 0; i < pixelData.length; i++) out[i] = pixelData[i] * s;
  return out;
}

/** 2D-поле дозы кадра f (row-major, длина cols*rows). */
export function doseSlice(grid, cols, rows, f) {
  const per = cols * rows;
  return grid.subarray(f * per, f * per + per);
}

export function doseStats(grid) {
  let mn = Infinity, mx = -Infinity;
  for (let i = 0; i < grid.length; i++) { const v = grid[i]; if (v < mn) mn = v; if (v > mx) mx = v; }
  return { min: mn, max: mx };
}

/** Уровни изодоз в Гр по процентам от опорной дозы (например, назначенной). */
export function percentLevels(refDoseGy, percents = [95, 90, 80, 50, 30]) {
  return percents.map(p => ({ percent: p, gy: refDoseGy * p / 100 }));
}

// ── marching squares ────────────────────────────────────────────────────────
/**
 * Изодозные отрезки уровня level для 2D-поля field (w×h, row-major).
 * Координаты — в пикселях (col,row), с суб-пиксельной интерполяцией по рёбрам.
 * @returns {number[][]} массив отрезков [x0,y0,x1,y1]
 */
export function isodoseSegments(field, w, h, level) {
  const segs = [];
  const at = (x, y) => field[y * w + x];
  const interp = (xa, ya, va, xb, yb, vb) => {
    const t = (level - va) / ((vb - va) || 1e-12);
    return [xa + t * (xb - xa), ya + t * (yb - ya)];
  };
  for (let y = 0; y < h - 1; y++) {
    for (let x = 0; x < w - 1; x++) {
      const tl = at(x, y), tr = at(x + 1, y), br = at(x + 1, y + 1), bl = at(x, y + 1);
      let code = 0;
      if (tl >= level) code |= 8;
      if (tr >= level) code |= 4;
      if (br >= level) code |= 2;
      if (bl >= level) code |= 1;
      if (code === 0 || code === 15) continue;

      // точки на рёбрах: T(верх), R(право), B(низ), L(лево)
      const T = () => interp(x, y, tl, x + 1, y, tr);
      const R = () => interp(x + 1, y, tr, x + 1, y + 1, br);
      const B = () => interp(x, y + 1, bl, x + 1, y + 1, br);
      const L = () => interp(x, y, tl, x, y + 1, bl);
      const push = (a, b) => segs.push([a[0], a[1], b[0], b[1]]);

      switch (code) {
        case 1: push(L(), B()); break;
        case 2: push(B(), R()); break;
        case 3: push(L(), R()); break;
        case 4: push(T(), R()); break;
        case 6: push(T(), B()); break;
        case 7: push(L(), T()); break;
        case 8: push(T(), L()); break;
        case 9: push(T(), B()); break;
        case 11: push(T(), R()); break;
        case 12: push(L(), R()); break;
        case 13: push(B(), R()); break;
        case 14: push(L(), B()); break;
        case 5: { // седло
          const c = (tl + tr + br + bl) / 4;
          if (c >= level) { push(L(), T()); push(B(), R()); }
          else { push(L(), B()); push(T(), R()); }
          break;
        }
        case 10: {
          const c = (tl + tr + br + bl) / 4;
          if (c >= level) { push(T(), R()); push(L(), B()); }
          else { push(T(), L()); push(B(), R()); }
          break;
        }
      }
    }
  }
  return segs;
}

/**
 * Сшить отрезки в полилинии, склеивая по общим концам.
 *
 * [6.7.37] Наращиваем в ОБЕ стороны. Раньше рост шёл только «от хвоста»: если
 * первый найденный отрезок оказывался в СЕРЕДИНЕ незамкнутой изолинии (а так
 * бывает всегда, когда изодоза упирается в край дозовой сетки), вторая половина
 * оставалась брошенной и начинала новую полилинию — и так каскадом. Проверка на
 * U-образной изолинии: 9 обрывков вместо одной линии.
 * Заодно выбрасываем вырожденные отрезки нулевой длины (появляются, когда доза
 * в узле сетки в точности равна уровню) — они рисовались точками-призраками.
 */
export function stitchSegments(segs, tol = 1e-4) {
  const rem = [];
  for (const s of segs) {
    if (Math.abs(s[2] - s[0]) < tol && Math.abs(s[3] - s[1]) < tol) continue;  // вырожденный
    rem.push(s);
  }
  const kx = (x) => Math.round(x / tol);
  const map = new Map();
  rem.forEach((s, i) => {
    for (const k of [`${kx(s[0])},${kx(s[1])}`, `${kx(s[2])},${kx(s[3])}`]) {
      let a = map.get(k); if (!a) { a = []; map.set(k, a); }
      a.push(i);
    }
  });
  const used = new Array(rem.length).fill(false);
  const near = (ax, ay, bx, by) => Math.abs(ax - bx) < tol && Math.abs(ay - by) < tol;

  // Неиспользованный отрезок, примыкающий к (x,y) → [индекс, противоположный конец].
  // Смотрим и соседние ячейки ключа: точка может лечь на границу округления.
  const findNext = (x, y) => {
    const bx = kx(x), by = kx(y);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const cand = map.get(`${bx + dx},${by + dy}`);
        if (!cand) continue;
        for (const j of cand) {
          if (used[j]) continue;
          const s = rem[j];
          if (near(s[0], s[1], x, y)) return [j, [s[2], s[3]]];
          if (near(s[2], s[3], x, y)) return [j, [s[0], s[1]]];
        }
      }
    }
    return null;
  };

  const lines = [];
  for (let i = 0; i < rem.length; i++) {
    if (used[i]) continue;
    used[i] = true;
    const line = [[rem[i][0], rem[i][1]], [rem[i][2], rem[i][3]]];
    for (;;) {                                   // вперёд от хвоста
      const e = line[line.length - 1];
      const nx = findNext(e[0], e[1]);
      if (!nx) break;
      used[nx[0]] = true; line.push(nx[1]);
    }
    for (;;) {                                   // назад от головы
      const h = line[0];
      const nx = findNext(h[0], h[1]);
      if (!nx) break;
      used[nx[0]] = true; line.unshift(nx[1]);
    }
    lines.push(line);
  }
  return lines;
}

// ── геометрия дозовой сетки ─────────────────────────────────────────────────
function cross(a, b) {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

// ── GridFrameOffsetVector: смещения кадров вдоль нормали ─────────────────────
// [6.7.37] Раньше смещение брали как frameOffsets[frame] напрямую. Два изъяна:
//   1) при ДРОБНОМ frame (а marching cubes спрашивает именно дробные — вершины
//      треугольников лежат между кадрами) получалось undefined → «|| 0», то есть
//      ВСЕ промежуточные вершины схлопывались на плоскость нулевого кадра.
//      Проверка: изоповерхность сферы R=24 мм давала объём −59 % и вершины,
//      разбросанные по радиусам 24…46 мм;
//   2) знак: DICOM допускает УБЫВАЮЩИЙ вектор смещений, а обратный перевод делил
//      на Math.abs(шаг) → мир→индекс возвращал кадр −1 вместо +1.
// Ниже — честная кусочно-линейная функция и её точное обращение.

const _offCache = new WeakMap();
function _offMeta(o) {
  if (!o || !o.length) return { n: 0, uniform: true, step: 1, first: 0 };
  let m = _offCache.get(o);
  if (m) return m;
  const n = o.length;
  const step = n > 1 ? (o[n - 1] - o[0]) / (n - 1) : 0;
  let uniform = true;
  if (n > 1) {
    const tol = Math.max(1e-6, Math.abs(step) * 1e-4);
    for (let i = 1; i < n - 1; i++) {
      if (Math.abs(o[i] - (o[0] + i * step)) > tol) { uniform = false; break; }
    }
  }
  m = { n, uniform, step: step || 1, first: o[0] };
  _offCache.set(o, m);
  return m;
}

/** Смещение вдоль нормали для ЛЮБОГО (в т.ч. дробного) индекса кадра. */
export function frameOffsetAt(geom, frame) {
  const o = geom.frameOffsets;
  const m = _offMeta(o);
  if (!m.n) return frame;                                   // вектора нет → шаг 1 мм
  if (m.n === 1) return o[0];
  if (m.uniform) return m.first + frame * m.step;
  const last = m.n - 1;
  if (frame <= 0) return o[0] + frame * (o[1] - o[0]);       // экстраполяция вниз
  if (frame >= last) return o[last] + (frame - last) * (o[last] - o[last - 1]);
  const i = Math.floor(frame), t = frame - i;
  return o[i] * (1 - t) + o[i + 1] * t;
}

/** Обратное к frameOffsetAt: смещение вдоль нормали → дробный индекс кадра. */
export function frameIndexAt(geom, off) {
  const o = geom.frameOffsets;
  const m = _offMeta(o);
  if (!m.n) return off;
  if (m.n === 1) return 0;
  if (m.uniform) return (off - m.first) / m.step;
  const last = m.n - 1;
  const inc = o[last] >= o[0];
  const reached = (a, b) => (inc ? a >= b : a <= b);
  if (!reached(off, o[0])) { const s = o[1] - o[0]; return s ? (off - o[0]) / s : 0; }
  for (let i = 1; i <= last; i++) {
    if (reached(o[i], off)) { const s = o[i] - o[i - 1]; return s ? (i - 1) + (off - o[i - 1]) / s : i - 1; }
  }
  const s = o[last] - o[last - 1];
  return s ? last + (off - o[last]) / s : last;
}

/**
 * (col,row,frame) → мировые координаты (LPS). frame может быть дробным.
 * geom: { ipp:[3], iop:[6], pixelSpacing:[rowSp,colSp], frameOffsets:number[] }
 *   iop = [rowDir(3), colDir(3)]; PixelSpacing[0]=между рядами (вдоль colDir),
 *   [1]=между столбцами (вдоль rowDir). frameOffsets — сдвиги кадров вдоль нормали.
 */
export function doseGridPixelToWorld(geom, col, row, frame) {
  const rowDir = geom.iop.slice(0, 3);
  const colDir = geom.iop.slice(3, 6);
  const nrm = cross(rowDir, colDir);
  const ps = geom.pixelSpacing || [1, 1];
  const off = frameOffsetAt(geom, frame);
  const a = col * ps[1], b = row * ps[0];
  return [
    geom.ipp[0] + a * rowDir[0] + b * colDir[0] + off * nrm[0],
    geom.ipp[1] + a * rowDir[1] + b * colDir[1] + off * nrm[1],
    geom.ipp[2] + a * rowDir[2] + b * colDir[2] + off * nrm[2],
  ];
}

/** Полилинии изодозы кадра f в МИРОВЫХ координатах (для наложения на срезы). */
export function isodoseWorldLines(field, w, h, level, geom, frame) {
  const segs = isodoseSegments(field, w, h, level);
  const lines = stitchSegments(segs);
  return lines.map(line => line.map(([c, r]) => doseGridPixelToWorld(geom, c, r, frame)));
}

// ── произвольная плоскость MPR ───────────────────────────────────────────────
function _dot3(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }

/**
 * Шаг между кадрами дозы, ВСЕГДА положительный — величина, а не направление.
 * Ею считают объём воксела, поэтому знак здесь недопустим. Для неравномерного
 * вектора смещений берём средний шаг, а не разность первых двух.
 */
export function frameSpacing(geom) {
  const m = _offMeta(geom.frameOffsets);
  return m.n > 1 ? (Math.abs(m.step) || 1) : 1;
}

/** Мир → дробный индекс (col,row,frame) дозовой сетки. */
export function doseWorldToIndex(geom, w) {
  const rowDir = geom.iop.slice(0, 3), colDir = geom.iop.slice(3, 6);
  const nrm = cross(rowDir, colDir);
  const ps = geom.pixelSpacing || [1, 1];
  const d = [w[0] - geom.ipp[0], w[1] - geom.ipp[1], w[2] - geom.ipp[2]];
  return [_dot3(d, rowDir) / ps[1], _dot3(d, colDir) / ps[0], frameIndexAt(geom, _dot3(d, nrm))];
}

/** Трилинейная доза в мировой точке (вне сетки → 0). */
export function sampleDose(dose, w) {
  const [c, r, f] = doseWorldToIndex(dose.geom, w);
  const { cols, rows, frames, grid } = dose;
  if (c < 0 || r < 0 || f < 0 || c > cols - 1 || r > rows - 1 || f > frames - 1) return 0;
  const c0 = Math.floor(c), r0 = Math.floor(r), f0 = Math.floor(f);
  const c1 = Math.min(c0 + 1, cols - 1), r1 = Math.min(r0 + 1, rows - 1), f1 = Math.min(f0 + 1, frames - 1);
  const fc = c - c0, fr = r - r0, ff = f - f0;
  const at = (ci, ri, fi) => grid[ci + cols * (ri + rows * fi)];
  const l = (a, b, t) => a * (1 - t) + b * t;
  const v00 = l(at(c0, r0, f0), at(c1, r0, f0), fc);
  const v10 = l(at(c0, r1, f0), at(c1, r1, f0), fc);
  const v01 = l(at(c0, r0, f1), at(c1, r0, f1), fc);
  const v11 = l(at(c0, r1, f1), at(c1, r1, f1), fc);
  return l(l(v00, v10, fr), l(v01, v11, fr), ff);
}

/**
 * Изодозные полилинии уровня level на ПРОИЗВОЛЬНОЙ плоскости MPR.
 * Плоскость: центр `o`, оси `uDir`,`vDir` (единичные), поле nu×nv точек шагом step.
 * @returns {number[][][]} полилинии мировых точек [x,y,z]
 */
export function isodoseOnPlane(dose, o, uDir, vDir, nu, nv, step, level) {
  const field = new Float32Array(nu * nv);
  const half_u = (nu - 1) / 2, half_v = (nv - 1) / 2;
  const toWorld = (i, j) => {
    const a = (i - half_u) * step, b = (j - half_v) * step;
    return [o[0] + a * uDir[0] + b * vDir[0], o[1] + a * uDir[1] + b * vDir[1], o[2] + a * uDir[2] + b * vDir[2]];
  };
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) field[j * nu + i] = sampleDose(dose, toWorld(i, j));
  }
  const segs = isodoseSegments(field, nu, nv, level);
  const lines = stitchSegments(segs);
  return lines.map(line => line.map(([ci, cj]) => toWorld(ci, cj)));
}
